import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CreditCard, Loader2, ShieldCheck, Sparkles, XCircle, RotateCcw, Crown, Gift } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const STRIPE_PORTAL_URL = "https://billing.stripe.com/p/login/eVqeVcdlgfP5egNgDJ3cc00";

const T = {
  fr: {
    title: "Mon abonnement", subtitle: "Gère ton forfait, ta facturation et ton accès — en toute liberté.",
    current: "Forfait actuel", status: "Statut", renews: "Prochaine échéance", billed: "Facturé",
    perMonth: "par mois", perYear: "par an", active: "Actif", canceling: "Se termine à la fin de la période",
    endsSoon: "Ton abonnement restera actif jusqu'à la fin de la période en cours, puis passera au forfait gratuit.",
    admin: "Accès administrateur — illimité", reward: "Bonus parrainage actif",
    free: "Tu es sur le forfait gratuit Souffle. Passe à un forfait pour explorer sans limite.",
    viewPlans: "Voir les forfaits", cancel: "Annuler l'abonnement", confirmCancel: "Confirmer l'annulation",
    keep: "Garder mon abonnement", resume: "Reprendre l'abonnement",
    canceled: "Abonnement annulé — actif jusqu'à la fin de la période.", resumed: "Abonnement repris ✨", err: "Une erreur est survenue.",
    managePortal: "Gérer mon abonnement", portalOpen: "Ouvrir mon espace Stripe",
    plans: { souffle: "Souffle", elan: "L'Élan", cocon: "Le Cocoon", free: "Souffle" },
  },
  en: {
    title: "My subscription", subtitle: "Manage your plan, billing and access — freely.",
    current: "Current plan", status: "Status", renews: "Next renewal", billed: "Billed",
    perMonth: "per month", perYear: "per year", active: "Active", canceling: "Ends at period's end",
    endsSoon: "Your subscription stays active until the end of the current period, then switches to the free plan.",
    admin: "Administrator access — unlimited", reward: "Referral bonus active",
    free: "You're on the free Souffle plan. Upgrade to explore without limits.",
    viewPlans: "See the plans", cancel: "Cancel subscription", confirmCancel: "Confirm cancellation",
    keep: "Keep my subscription", resume: "Resume subscription",
    canceled: "Subscription canceled — active until period end.", resumed: "Subscription resumed ✨", err: "Something went wrong.",
    managePortal: "Manage my subscription", portalOpen: "Open my Stripe space",
    plans: { souffle: "Souffle", elan: "L'Élan", cocon: "Le Cocoon", free: "Souffle" },
  },
  es: {
    title: "Mi suscripción", subtitle: "Gestiona tu plan, tu facturación y tu acceso — con total libertad.",
    current: "Plan actual", status: "Estado", renews: "Próxima renovación", billed: "Facturado",
    perMonth: "al mes", perYear: "al año", active: "Activo", canceling: "Termina al final del período",
    endsSoon: "Tu suscripción seguirá activa hasta el final del período actual y luego pasará al plan gratuito.",
    admin: "Acceso de administrador — ilimitado", reward: "Bono de recomendación activo",
    free: "Estás en el plan gratuito Souffle. Mejora tu plan para explorar sin límites.",
    viewPlans: "Ver los planes", cancel: "Cancelar suscripción", confirmCancel: "Confirmar cancelación",
    keep: "Mantener mi suscripción", resume: "Reanudar suscripción",
    canceled: "Suscripción cancelada — activa hasta el final del período.", resumed: "Suscripción reanudada ✨", err: "Ha ocurrido un error.",
    managePortal: "Gestionar mi suscripción", portalOpen: "Abrir mi espacio Stripe",
    plans: { souffle: "Souffle", elan: "L'Élan", cocon: "Le Cocoon", free: "Souffle" },
  },
  nl: {
    title: "Mijn abonnement", subtitle: "Beheer je plan, facturering en toegang — in alle vrijheid.",
    current: "Huidig plan", status: "Status", renews: "Volgende verlenging", billed: "Gefactureerd",
    perMonth: "per maand", perYear: "per jaar", active: "Actief", canceling: "Eindigt aan het einde van de periode",
    endsSoon: "Je abonnement blijft actief tot het einde van de huidige periode en gaat daarna naar het gratis plan.",
    admin: "Beheerderstoegang — onbeperkt", reward: "Verwijzingsbonus actief",
    free: "Je zit op het gratis Souffle-plan. Upgrade om zonder limieten te verkennen.",
    viewPlans: "Bekijk de abonnementen", cancel: "Abonnement opzeggen", confirmCancel: "Opzegging bevestigen",
    keep: "Mijn abonnement behouden", resume: "Abonnement hervatten",
    canceled: "Abonnement opgezegd — actief tot einde periode.", resumed: "Abonnement hervat ✨", err: "Er is iets misgegaan.",
    managePortal: "Mijn abonnement beheren", portalOpen: "Mijn Stripe-ruimte openen",
    plans: { souffle: "Souffle", elan: "L'Élan", cocon: "Le Cocoon", free: "Souffle" },
  },
};

const PLAN_STYLE = {
  cocon: { icon: Crown, ring: "border-amber-400/40 bg-amber-400/10 text-amber-300" },
  elan: { icon: Sparkles, ring: "border-primary/40 bg-primary/10 text-primary" },
  souffle: { icon: ShieldCheck, ring: "border-white/15 bg-white/5 text-foreground/70" },
};

function formatDate(v, lng) {
  if (!v) return null;
  try {
    const d = typeof v === "number" ? new Date(v * 1000) : new Date(v);
    if (isNaN(d.getTime())) return null;
    return d.toLocaleDateString(lng || "fr-FR", { day: "numeric", month: "long", year: "numeric" });
  } catch { return null; }
}

export function MonAbonnement() {
  const { i18n } = useTranslation();
  const lang = (i18n.language || "fr").slice(0, 2);
  const L = T[lang] || T.fr;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/payments/subscription")
      .then(({ data }) => setData(data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const doCancel = async () => {
    setBusy(true);
    try { await api.post("/payments/subscription/cancel"); toast.success(L.canceled); setConfirming(false); load(); }
    catch { toast.error(L.err); }
    finally { setBusy(false); }
  };
  const doResume = async () => {
    setBusy(true);
    try { await api.post("/payments/subscription/resume"); toast.success(L.resumed); load(); }
    catch { toast.error(L.err); }
    finally { setBusy(false); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-32" data-testid="mon-abonnement">
        <Loader2 className="animate-spin text-primary" size={28} />
      </div>
    );
  }

  const planKey = data?.plan === "cocon" ? "cocon" : data?.plan === "elan" ? "elan" : "souffle";
  const style = PLAN_STYLE[planKey];
  const PlanIcon = style.icon;
  const sub = data?.subscription;
  const isAdmin = data?.is_admin;
  const renew = formatDate(sub?.current_period_end, i18n.language);
  const price = sub?.amount != null
    ? (sub.amount / 100).toLocaleString(i18n.language, { style: "currency", currency: (sub.currency || "eur").toUpperCase() })
    : null;

  return (
    <div className="max-w-2xl mx-auto px-6 py-10" data-testid="mon-abonnement">
      <div className="text-center mb-10">
        <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/10 border border-primary/25 text-primary text-[11px] uppercase tracking-[0.24em] mb-5">
          <CreditCard size={13} /> {L.title}
        </span>
        <h1 className="font-display text-3xl sm:text-4xl italic text-gradient-orange">{L.title}</h1>
        <p className="text-foreground/50 mt-3 leading-relaxed max-w-md mx-auto">{L.subtitle}</p>
      </div>

      <div className="glass rounded-3xl border border-white/10 p-7 sm:p-9">
        {/* plan header */}
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <span className={`w-12 h-12 rounded-2xl border flex items-center justify-center ${style.ring}`}>
              <PlanIcon size={22} />
            </span>
            <div>
              <p className="text-[11px] uppercase tracking-widest text-foreground/40">{L.current}</p>
              <p className="font-display text-2xl" data-testid="sub-plan-badge">{L.plans[planKey]}</p>
            </div>
          </div>
          {sub && (
            <span className={`px-3 py-1 rounded-full text-xs border ${sub.cancel_at_period_end ? "border-amber-400/40 bg-amber-400/10 text-amber-300" : "border-emerald-400/30 bg-emerald-400/10 text-emerald-300"}`} data-testid="sub-status">
              {sub.cancel_at_period_end ? L.canceling : L.active}
            </span>
          )}
        </div>

        {isAdmin && (
          <div className="mt-6 flex items-center gap-2 text-sm text-amber-300/90" data-testid="sub-admin-note">
            <Crown size={15} /> {L.admin}
          </div>
        )}
        {data?.reward_active && (
          <div className="mt-3 flex items-center gap-2 text-sm text-primary/90">
            <Gift size={15} /> {L.reward}
          </div>
        )}

        {/* details */}
        {sub ? (
          <div className="mt-7 space-y-3 border-t border-white/8 pt-6 text-sm">
            {price && (
              <div className="flex justify-between">
                <span className="text-foreground/45">{L.billed}</span>
                <span className="text-foreground/90 font-medium">{price} · {sub.interval === "year" ? L.perYear : L.perMonth}</span>
              </div>
            )}
            {renew && (
              <div className="flex justify-between">
                <span className="text-foreground/45">{L.renews}</span>
                <span className="text-foreground/90 font-medium">{renew}</span>
              </div>
            )}
            {sub.cancel_at_period_end && (
              <p className="text-amber-300/80 text-sm leading-relaxed pt-2">{L.endsSoon}</p>
            )}
          </div>
        ) : (
          !isAdmin && <p className="mt-7 border-t border-white/8 pt-6 text-foreground/55 leading-relaxed">{L.free}</p>
        )}

        {/* actions */}
        <div className="mt-8 flex flex-col sm:flex-row gap-3">
          <a
            href={STRIPE_PORTAL_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="sub-stripe-portal-link"
            className="btn-frost-orange inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full font-semibold text-sm"
          >
            <ShieldCheck size={15} /> {L.portalOpen}
          </a>
          {!sub && !isAdmin && (
            <Link to="/tarifs" data-testid="sub-view-plans-btn" className="btn-frost-orange inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full font-semibold text-sm">
              <Sparkles size={15} /> {L.viewPlans}
            </Link>
          )}

          {sub && !sub.cancel_at_period_end && !confirming && (
            <button onClick={() => setConfirming(true)} data-testid="sub-cancel-btn" disabled={busy}
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full font-semibold text-sm border border-white/15 text-foreground/70 hover:border-red-400/40 hover:text-red-300 transition-colors disabled:opacity-60">
              <XCircle size={15} /> {L.cancel}
            </button>
          )}

          {sub && !sub.cancel_at_period_end && confirming && (
            <>
              <button onClick={doCancel} data-testid="sub-cancel-confirm-btn" disabled={busy}
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full font-semibold text-sm bg-red-500/15 border border-red-400/40 text-red-300 hover:bg-red-500/25 transition-colors disabled:opacity-60">
                {busy ? <Loader2 size={15} className="animate-spin" /> : <XCircle size={15} />} {L.confirmCancel}
              </button>
              <button onClick={() => setConfirming(false)} disabled={busy}
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full font-semibold text-sm border border-white/15 text-foreground/70 hover:border-primary/40 transition-colors">
                {L.keep}
              </button>
            </>
          )}

          {sub && sub.cancel_at_period_end && (
            <button onClick={doResume} data-testid="sub-resume-btn" disabled={busy}
              className="btn-frost-orange inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full font-semibold text-sm disabled:opacity-60">
              {busy ? <Loader2 size={15} className="animate-spin" /> : <RotateCcw size={15} />} {L.resume}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
